import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row
} from "reactstrap";
import { Link } from "react-router-dom";
import classnames from 'classnames';
import { getNavigation } from "../../store/actions/navigation";
import {
    FaQuestion,
    FaExternalLinkAlt
} from "react-icons/fa";
import { getLatestNews } from "../../store/actions/news";
import CustomNewsCarousel from "./NewsCarouselModel";
import { getUserLinks, getUserQuickRoutes, getUserProfile } from "../../store/actions/user";
import { DashboardWrapper } from "../../utils/components";
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { menuIcons } from './constants';
import MobileMenuList from './components/MobileMenuList';
import CommonlyUsedForms from './components/CommonlyUsedForms';
import DashboardSkeleton from './components/DashboardSkeleton';
import DashboardHero from './components/DashboardHero';
import DashboardFooter from './components/DashboardFooter';
import './styles.css';

const Dashboard = () => {
    const dispatch = useDispatch();
    const navigation = useSelector((state) => state.navigation.list);
    const news = useSelector((state) => state.news.list);
    const quickLinks = useSelector((state) => state.users.userLinks);
    const modules = useSelector((state) => state.users.userModules);
    const userProfile = useSelector((state) => state.users.userProfile);

    const isMobileTiny = useMediaQuery({ maxWidth: 580 });
    const isMobile = useMediaQuery({ maxWidth: 575.98 });
    const isTablet = useMediaQuery({ minWidth: 576, maxWidth: 991.98 });
    const isDesktop = useMediaQuery({ minWidth: 992 });
    const isLargeDesktop = useMediaQuery({ minWidth: 1200 });

    const [uniqueMenuNames, setUniqueMenuNames] = useState([]);
    const [activeTab, setActiveTab] = useState('1');
    const [isLoading, setIsLoading] = useState(true);
    const [pageLoaded, setPageLoaded] = useState(false);

    const [fabTooltipOpen, setFabTooltipOpen] = useState(false);
    const handleFabClick = () => {
        window.location.href = '/incident-reporting';
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            await Promise.all([
                dispatch(getUserLinks()),
                dispatch(getUserQuickRoutes()),
                dispatch(getNavigation()),
                dispatch(getLatestNews()),
                dispatch(getUserProfile()),
            ]);
            setIsLoading(false);
            
            setTimeout(() => {
                setPageLoaded(true);
            }, 100);
        };

        fetchData();
    }, [dispatch]);

    useEffect(() => {
        if (navigation) {
            const uniqueNames = [...new Set(navigation.map((item) => item.menu_name))];
            setUniqueMenuNames(uniqueNames);
        }
    }, [navigation]);

    const menuItems = uniqueMenuNames.map((name) => ({
        name: name,
        ...menuIcons[name] || {icon: <FaQuestion/>, color: "#95a5a6", ethicalNote: "Ask when uncertain"},
    }));

    const toggle = tab => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    const getGridTemplateColumns = () => {
        if (isMobile && !isMobileTiny) return 'repeat(2, 1fr)';
        if (isTablet) return 'repeat(3, 1fr)';
        if (isDesktop && !isLargeDesktop) return 'repeat(4, 1fr)';
        return 'repeat(4, 1fr)';
    };

    return (
        <DashboardWrapper>
            <div className="page-content">
                <Container fluid className={isMobile ? "px-2" : "px-3"}>
                    {isLoading ? (
                        <DashboardSkeleton />
                    ) : (
                        <CSSTransition
                            in={pageLoaded}
                            timeout={400}
                            classNames={{
                                enter: 'fade-enter',
                                enterActive: 'fade-enter-active',
                            }}
                            appear={true}
                        >
                            <div>
                                <DashboardHero userProfile={userProfile} />
                                
                                <Card className="dashboard-card mb-4">
                                    <CardBody className="card-padding">
                                        <h2 className="card-title mb-4">Welcome to M-SPHERE</h2>

                                        {isMobileTiny ? (
                                            <div className="mobile-menu-container">
                                                <MobileMenuList menuItems={menuItems}/>
                                            </div>
                                        ) : (
                                            <nav
                                                className="menu-container"
                                                aria-label="Main navigation"
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: getGridTemplateColumns(),
                                                    gap: isMobile ? '0.75rem' : '1.5rem',
                                                    width: '100%',
                                                    marginBottom: '2.5rem',
                                                }}
                                            >
                                                {menuItems.map((item, index) => (
                                                    <CSSTransition
                                                        key={index}
                                                        in={true}
                                                        appear={true}
                                                        timeout={200 + (index * 50)}
                                                        classNames="fade-up"
                                                    >
                                                        <Link
                                                            to={`/${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                                                            className="menu-item focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                                                            aria-label={`${item.name === "Metrics" ? "Dashboard" : item.name} section${item.ethicalNote ? ` - ${item.ethicalNote}` : ''}`}
                                                            tabIndex={0}
                                                            style={{
                                                                textDecoration: 'none',
                                                                color: 'var(--text-primary, #212529)',
                                                                willChange: 'transform, box-shadow',
                                                            }}
                                                        >
                                                            <div className="icon-wrapper" aria-hidden="true">
                                                                <div
                                                                    className="icon"
                                                                    style={{
                                                                        color: item.color,
                                                                        fontSize: isMobile ? "2rem" : "2.3rem",
                                                                        marginBottom: isMobile ? '0.5rem' : '0.7rem',
                                                                        transition: 'color 0.18s, transform 0.18s',
                                                                        display: 'flex',
                                                                        justifyContent: 'center',
                                                                        alignItems: 'center'
                                                                    }}
                                                                >
                                                                    {item.icon}
                                                                </div>
                                                            </div>
                                                            <div className="text">
                                                                {item.name === "Metrics" ? "Dashboard" : item.name}
                                                            </div>
                                                            <div className="menu-description">
                                                                {item.ethicalNote}
                                                            </div>
                                                        </Link>
                                                    </CSSTransition>
                                                ))}
                                            </nav>
                                        )}
                                    </CardBody>
                                </Card>

                                <Row className={isMobile ? "g-2" : "g-3"}>
                                    <Col xl={8} lg={7} md={12} className={isMobile ? "mb-2" : "mb-3"}>
                                        <CSSTransition
                                            in={true}
                                            appear={true}
                                            timeout={300}
                                            classNames="fade-up"
                                        >
                                            <Card className="dashboard-card mb-3">
                                                <CardBody className="card-padding">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <h3 className="card-title mb-0">Latest News & Updates</h3>
                                                    </div>
                                                    <CustomNewsCarousel news={news} />
                                                </CardBody>
                                            </Card>
                                        </CSSTransition>
                                    </Col>
                                    <Col xl={4} lg={5} md={12}>
                                        <CSSTransition
                                            in={true}
                                            appear={true}
                                            timeout={400}
                                            classNames="fade-up"
                                        >
                                            <Card className="dashboard-card">
                                                <CardBody className="card-padding">
                                                    <h3 className="card-title mb-3">Quick Access</h3>
                                                    <div className="nav-tabs mb-3">
                                                        <a
                                                            className={classnames("nav-link", {active: activeTab === '1'})}
                                                            onClick={() => { toggle('1'); }}
                                                            role="tab"
                                                            aria-selected={activeTab === '1'}
                                                            aria-controls="forms-panel"
                                                        >
                                                            My Forms
                                                        </a>
                                                        <a
                                                            className={classnames("nav-link", {active: activeTab === '2'})}
                                                            onClick={() => { toggle('2'); }}
                                                            role="tab"
                                                            aria-selected={activeTab === '2'}
                                                            aria-controls="links-panel"
                                                        >
                                                            External Links
                                                        </a>
                                                    </div>
                                                    <div className="tab-content">
                                                        <div
                                                            className={classnames({active: activeTab === '1'})}
                                                            role="tabpanel"
                                                            id="forms-panel"
                                                            aria-labelledby="forms-tab"
                                                            style={{display: activeTab === '1' ? 'block' : 'none'}}
                                                        >
                                                            <CommonlyUsedForms modules={modules} navigationData={navigation}/>
                                                        </div>
                                                        <div
                                                            className={classnames({active: activeTab === '2'})}
                                                            role="tabpanel"
                                                            id="links-panel"
                                                            aria-labelledby="links-tab"
                                                            style={{display: activeTab === '2' ? 'block' : 'none'}}
                                                        >
                                                            {quickLinks && quickLinks.length > 0 ? (
                                                                <TransitionGroup
                                                                    className="list-group"
                                                                    style={{
                                                                        maxHeight: isMobile ? '200px' : isTablet ? '250px' : '300px',
                                                                        overflowY: 'auto',
                                                                        overflowX: 'hidden'
                                                                    }}
                                                                    aria-label="External links"
                                                                >
                                                                    {quickLinks.map((link, index) => (
                                                                        <CSSTransition
                                                                            key={link.id}
                                                                            timeout={200 + (index * 50)}
                                                                            classNames="item"
                                                                        >
                                                                            <div className="list-group-item">
                                                                                <a href={link.link} target="_blank" rel="noopener noreferrer" className="item-text">
                                                                                    <div className="item-icon">
                                                                                        <FaExternalLinkAlt />
                                                                                    </div>
                                                                                    {link.name}
                                                                                    <FaExternalLinkAlt className="external-icon" aria-hidden="true" />
                                                                                </a>
                                                                            </div>
                                                                        </CSSTransition>
                                                                    ))}
                                                                </TransitionGroup>
                                                            ) : (
                                                                <div className="text-center py-4 text-muted">
                                                                    <p>No external links available</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </CSSTransition>
                                    </Col>
                                </Row>
                            </div>
                        </CSSTransition>
                    )}
                </Container>
            </div>
            <DashboardFooter />
        </DashboardWrapper>
    );
};

export default Dashboard;